/** Canvas2D entity rendering — dispatches spawners and icon-type units. */
import { Faction, FACTION_COUNT } from '../../factions.js';
import { drawSpawner, drawByType, ICON_UNIT_TYPES } from './primitives.js';
import { isPortrait } from '../../device.js';
import { renderFightersBatched } from './fighters.js';
import { renderAiDebugStateMarker, renderAiTargetLines } from './overlays.js';

/** @type {Array<Array<object>>} Reusable per-frame icon-unit buckets, indexed by faction. */
const _iconUnitBuckets = Array.from({ length: FACTION_COUNT }, () => []);

/**
 * @param {import('../../renderer.js').Canvas2DRenderer} r
 * @param {number} timePassed
 */
export function renderEntities(r, timePassed) {
    var i, entity;
    const ctx = r.context;
    const time = r.entitySystem.time;
    const portrait = isPortrait();
    const drawOpts = { time, portrait };

    for (i = 0; i < r.entitySystem.spawners.length; i += 1) {
        drawSpawner(r.entitySystem.spawners[i], ctx, drawOpts);
    }

    r.effectSystem.updateByLayer(ctx, timePassed, 'ground');

    const entityRenderStart = performance.now();

    // Batch all fighters into Path2D buckets (one submit per colour/weight/alpha
    // combination instead of one submit per entity).
    renderFightersBatched(r);

    // Non-fighter units (frigates, carriers, corvettes, missiles) — few in number,
    // rendered in faction order for correct z-layering. One pass over the unit
    // array to bucket them; re-scanning all units (mostly fighters) once per
    // faction cost FACTION_COUNT × N iterations per frame.
    for (var faction = Faction.NEUTRAL; faction < FACTION_COUNT; faction++) {
        _iconUnitBuckets[faction].length = 0;
    }
    for (i = 0; i < r.entitySystem.units.length; i += 1) {
        entity = r.entitySystem.units[i];
        if (ICON_UNIT_TYPES.has(entity.type)) {
            _iconUnitBuckets[entity.faction].push(entity);
        }
    }
    ctx.lineWidth = 2;
    const aiDebug = r.game.isAiDebugMode && r.game.isAiDebugMode();
    for (faction = Faction.NEUTRAL; faction < FACTION_COUNT; faction++) {
        const bucket = _iconUnitBuckets[faction];
        if (bucket.length === 0) continue;
        ctx.strokeStyle = r.unitAttackColors[faction];
        ctx.fillStyle   = r.unitFactionColors[faction];
        for (i = 0; i < bucket.length; i += 1) {
            drawByType(bucket[i], ctx, drawOpts);
            if (aiDebug) {
                renderAiDebugStateMarker(r, bucket[i]);
            }
        }
    }

    r.game.entityRenderTimeMs = r.game.entityRenderTimeMs * 0.9 +
        (performance.now() - entityRenderStart) * 0.1;

    if (r.game.isAiDebugMode && r.game.isAiDebugMode()) {
        renderAiTargetLines(r);
    }
}
